import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';

const MIN_BLOCK_WIDTH = 300;
const GAP = ANALYSIS_TOOL_SPACING;
const NUM_COLUMNS = 2;

export interface BlockSpec {
  key: string;
  block: JSX.Element;
  minHeight: number;
  maxHeight?: number;
}

interface DashboardLayoutProps {
  expandedBlockKey?: string | null;
  blockSpecs: Array<BlockSpec>;
}

/**
 * Render the given blocks with a dashboard layout of up to 2 blocks per row
 *
 * This dashboard layout is inspired by Insights dashboards.
 */
export default function DashboardLayout(props: DashboardLayoutProps): JSX.Element {
  const expandedBlockSpec = props.blockSpecs.find((blockSpec) => blockSpec.key === props.expandedBlockKey);
  let layout;

  if (expandedBlockSpec != null) {
    layout = <div className={css(styles.expandedBlock)}>{expandedBlockSpec.block}</div>;
  } else {
    layout = (
      <div className={css(styles.grid)}>
        {props.blockSpecs.map(({block, minHeight, key, maxHeight}) => (
          <div key={key} style={{minHeight, maxHeight}}>
            {block}
          </div>
        ))}
      </div>
    );
  }

  return <div className={css(styles.layoutWrapper)}>{layout}</div>;
}

const styles = StyleSheet.create({
  expandedBlock: {
    height: '100%',
  },
  grid: {
    display: 'grid',
    gap: GAP,
    // This is essentially saying that we want to fit as many column as possible in our container such that the
    // columns are at least `MIN_BLOCK_WIDTH` wide and that we have at most `NUM_COLUMNS` columns. `repeat()`
    // with `auto-fit` is what defines this "fit as many columns as possible" behavior. (In this context, read
    // this as "repeat the sizing constraint of the second arg of `repeat()` however many times we can make it
    // fit in the row based on the container's size").
    //
    // To get the actual number of columns in each row, we use `minmax()` to define the allowable range of its
    // size. Min is whatever arg is smaller in `minmax()`. The base range is [`MIN_BLOCK_WIDTH`, `1fr`], meaning
    // that we want to repeat adding columns that are 1 fraction of the container's width wide where the number
    // of columns is a number such that 1 fraction of the width > MIN_BLOCK_WIDTH. To restrict the number of
    // columns to be less than or equal to `NUM_COLUMNS`, we use `max()` to add an additional restriction
    // to make it such that 1 fraction < ( size of the container / max columns). Since there's some gap between
    // columns, size of the container needs to be adjusted by the total gap between columns in this case.
    gridTemplateColumns: `
      repeat(
        auto-fit,
        minmax(
          max(
            ${MIN_BLOCK_WIDTH}px,
            calc((100% - ${NUM_COLUMNS}*${GAP}px)/${NUM_COLUMNS})
          ),
          1fr
        )
      )`,
  },
  layoutWrapper: {
    flexGrow: 1,
    padding: GAP,
  },
});
